import apiClient from './client';

// Paginated response type
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

// Organization types
export interface OrganizationAdmin {
  id: string;
  name: string;
  logo_url?: string;
  user_count: number;
  created_at: string;
}

export interface OrganizationCreateRequest {
  name: string;
}

export interface OrganizationUpdateRequest {
  name?: string;
}

// User types
export interface UserAdmin {
  id: string;
  email: string;
  full_name: string | null;
  is_active: boolean;
  organization_name: string | null;
  created_at: string;
  // 2FA fields
  totp_enabled: boolean;
  totp_grace_period_ends_at: string | null;
}

export interface UserCreateRequest {
  email: string;
  full_name?: string;
  org_id?: string;
}

export interface UserUpdateRequest {
  email?: string;
  full_name?: string;
  is_active?: boolean;
  org_id?: string;
}

export const adminApi = {
  // Organizations
  listOrganizations: async (
    page: number = 1,
    pageSize: number = 50,
    search?: string
  ): Promise<PaginatedResponse<OrganizationAdmin>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    if (search) {
      params.append('search', search);
    }
    const response = await apiClient.get<PaginatedResponse<OrganizationAdmin>>(
      `/admin/organizations?${params.toString()}`
    );
    return response.data;
  },

  createOrganization: async (
    data: OrganizationCreateRequest
  ): Promise<OrganizationAdmin> => {
    const response = await apiClient.post<OrganizationAdmin>(
      '/admin/organizations',
      data
    );
    return response.data;
  },

  updateOrganization: async (
    id: string,
    data: OrganizationUpdateRequest
  ): Promise<OrganizationAdmin> => {
    const response = await apiClient.put<OrganizationAdmin>(
      `/admin/organizations/${id}`,
      data
    );
    return response.data;
  },

  deleteOrganization: async (id: string): Promise<void> => {
    await apiClient.delete(`/admin/organizations/${id}`);
  },

  uploadOrganizationLogo: async (
    orgId: string,
    file: File
  ): Promise<OrganizationAdmin> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<OrganizationAdmin>(
      `/admin/organizations/${orgId}/logo`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  // Users
  listUsers: async (
    page: number = 1,
    pageSize: number = 50,
    search?: string
  ): Promise<PaginatedResponse<UserAdmin>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    if (search) {
      params.append('search', search);
    }
    const response = await apiClient.get<PaginatedResponse<UserAdmin>>(
      `/admin/users?${params.toString()}`
    );
    return response.data;
  },

  createUser: async (data: UserCreateRequest): Promise<UserAdmin> => {
    const response = await apiClient.post<UserAdmin>('/admin/users', data);
    return response.data;
  },

  updateUser: async (id: string, data: UserUpdateRequest): Promise<UserAdmin> => {
    const response = await apiClient.put<UserAdmin>(`/admin/users/${id}`, data);
    return response.data;
  },

  deleteUser: async (id: string): Promise<void> => {
    await apiClient.delete(`/admin/users/${id}`);
  },

  /**
   * Reset a user's 2FA settings
   * Disables 2FA and sets a new 7-day grace period
   */
  reset2FA: async (userId: string): Promise<UserAdmin> => {
    const response = await apiClient.post<UserAdmin>(
      `/admin/users/${userId}/reset-2fa`
    );
    return response.data;
  },

  /**
   * Extend a user's 2FA grace period by 7 days
   * Does not affect existing 2FA settings
   */
  extendGracePeriod: async (userId: string): Promise<UserAdmin> => {
    const response = await apiClient.post<UserAdmin>(
      `/admin/users/${userId}/extend-grace-period`
    );
    return response.data;
  },

  /**
   * Resend the welcome email to a user
   */
  resendWelcomeEmail: async (userId: string): Promise<UserAdmin> => {
    const response = await apiClient.post<UserAdmin>(
      `/admin/users/${userId}/resend-welcome`
    );
    return response.data;
  },
};
